import { getToken } from 'utils/helper-functions/getTokenFromUrl';

// Per-session memory of the tab switch.
//
// Once the player has switched tab in a multi-intent session, every request
// that takes the intent argument, every submission and the card routing check
// carry the active tab's value on both tabs. The session reply cannot tell
// Cashier that a switch happened, so the fact is remembered here, keyed by the
// session token, for the life of the browser tab (sessionStorage survives a
// reload of the same tab). The same memory records which tab was active, so a
// reload reopens it.
//
// Every storage access is guarded: when the browser refuses storage the memory
// still lives for the current page, and a reload simply starts as not switched.

export interface IntentSwitchMemory {
  switched: boolean;
  activeIntent?: string;
}

const STORAGE_KEY_PREFIX = 'cashier_intent_switch_';

const pageMemory: Record<string, IntentSwitchMemory> = {};

const storageKey = (token: string): string => `${STORAGE_KEY_PREFIX}${token}`;

const readStorage = (token: string): IntentSwitchMemory | null => {
  if (!token) return null;

  try {
    const raw = window.sessionStorage.getItem(storageKey(token));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;

    return {
      switched: parsed.switched === true,
      ...(typeof parsed.activeIntent === 'string' && { activeIntent: parsed.activeIntent }),
    };
  } catch (err) {
    return null;
  }
};

const writeStorage = (token: string, memory: IntentSwitchMemory): void => {
  if (!token) return;

  try {
    window.sessionStorage.setItem(storageKey(token), JSON.stringify(memory));
  } catch (err) {
    // Storage refused: the page memory still holds the switch for this page.
  }
};

/**
 * The remembered switch for a session token, or null when nothing was
 * remembered for it.
 */
export const readIntentSwitchMemory = (token: string = getToken()): IntentSwitchMemory | null =>
  pageMemory[token] ?? readStorage(token);

/**
 * Records that the player is on the tab of `activeIntent` after a switch (or a
 * reload that reopened a tab), and that the session therefore counts as
 * switched from now on.
 */
export const rememberIntentSwitch = (activeIntent: string, token: string = getToken()): void => {
  const memory: IntentSwitchMemory = { switched: true, activeIntent };

  pageMemory[token] = memory;
  writeStorage(token, memory);
};

/**
 * True once a tab switch was remembered for the session token.
 */
export const isIntentSwitchRemembered = (token: string = getToken()): boolean =>
  readIntentSwitchMemory(token)?.switched === true;

/**
 * Forgets the remembered switch. Used by tests; the runtime never forgets a
 * switch within a session.
 */
export const clearIntentSwitchMemory = (token: string = getToken()): void => {
  delete pageMemory[token];

  if (!token) return;

  try {
    window.sessionStorage.removeItem(storageKey(token));
  } catch (err) {
    // Nothing to clear.
  }
};
